import React, { useState, useEffect } from 'react';
import { Wrench, Search, AlertTriangle, Loader, Replace, Trash2, X, Edit2 } from 'lucide-react';
import { skillsService, SkillWithCounts } from '../../lib/skillsService';
import { useAuth } from '../../hooks/useAuth';

interface ReplaceSkillModalProps {
  isOpen: boolean;
  skill: SkillWithCounts | null;
  allSkills: SkillWithCounts[];
  onClose: () => void;
  onSuccess: () => void;
}

const ReplaceSkillModal: React.FC<ReplaceSkillModalProps> = ({ isOpen, skill, allSkills, onClose, onSuccess }) => {
  const { appUser } = useAuth();
  const [selectedSkill, setSelectedSkill] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editedName, setEditedName] = useState('');
  const [isReplacing, setIsReplacing] = useState(false);
  const [activeTab, setActiveTab] = useState<'edit' | 'replace'>('edit');
  const [error, setError] = useState('');

  useEffect(() => {
    if (skill) {
      setSelectedSkill('');
      setSearchTerm('');
      setEditedName(skill.skill);
      setActiveTab('edit');
    }
  }, [skill]);

  // Filter skills for dropdown (exclude current skill)
  const availableSkills = allSkills.filter(s => 
    s.skill !== skill?.skill &&
    s.skill.toLowerCase().includes(searchTerm.toLowerCase())
  ).slice(0, 10); // Limit to 10 results

  const handleEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!skill || !appUser) return;

    if (!editedName.trim()) {
      setError('Skill name cannot be empty');
      return;
    }

    if (editedName.trim() === skill.skill) {
      setError('Please enter a different name');
      return;
    }

    // Check if the new name already exists
    if (allSkills.some(s => s.skill.toLowerCase() === editedName.trim().toLowerCase() && s.skill !== skill.skill)) {
      setError('A skill with this name already exists');
      return;
    }

    setIsReplacing(true);
    setError('');

    try {
      const result = await skillsService.replaceSkill(skill.skill, editedName.trim(), appUser.email);
      
      if (result.errors.length > 0) {
        setError(`Updated ${result.updated} people, but encountered errors: ${result.errors.join(', ')}`);
      } else {
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to edit skill name');
    } finally {
      setIsReplacing(false);
    }
  };

  const handleReplace = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!skill || !appUser) return;

    if (!selectedSkill.trim()) {
      setError('Please select a skill to replace with');
      return;
    }

    setIsReplacing(true);
    setError('');

    try {
      const result = await skillsService.replaceSkill(skill.skill, selectedSkill, appUser.email);
      
      if (result.errors.length > 0) {
        setError(`Updated ${result.updated} people, but encountered errors: ${result.errors.join(', ')}`);
      } else {
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to replace skill');
    } finally {
      setIsReplacing(false);
    }
  };

  const handleDelete = async () => {
    if (!skill || !appUser) return;

    if (!window.confirm(`Are you sure you want to delete the skill "${skill.skill}"? This will remove it from ${skill.peopleWithSkill.length} people.`)) {
      return;
    }

    setIsReplacing(true);
    setError('');

    try {
      const result = await skillsService.deleteSkill(skill.skill, appUser.email);
      
      if (result.errors.length > 0) {
        setError(`Deleted from ${result.updated} people, but encountered errors: ${result.errors.join(', ')}`);
      } else {
        onSuccess();
        onClose();
      }
    } catch (error: any) {
      setError(error.message || 'Failed to delete skill');
    } finally {
      setIsReplacing(false);
    }
  };

  const handleClose = () => {
    if (isReplacing) return;
    setSelectedSkill('');
    setSearchTerm('');
    setEditedName('');
    setActiveTab('edit');
    setError('');
    onClose();
  };

  if (!isOpen || !skill) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg shadow-xl">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">Manage Skill: "{skill.skill}"</h2>
            <button
              onClick={handleClose}
              disabled={isReplacing}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('edit')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'edit'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Edit Name
            </button>
            <button
              onClick={() => setActiveTab('replace')}
              className={`px-4 py-2 text-sm font-medium border-b-2 ${
                activeTab === 'replace'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Replace with Existing
            </button>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h3 className="font-medium mb-3">Current Usage</h3>
            <div className="text-sm space-y-2">
              <div className="flex justify-between">
                <span>Total usage:</span>
                <span className="font-medium">{skill.totalCount}</span>
              </div>
              <div className="flex justify-between">
                <span>People with skill:</span>
                <span className="font-medium">{skill.peopleWithSkill.length}</span>
              </div>
              
              <div className="mt-3 pt-3 border-t border-gray-200">
                <div className="text-xs font-medium text-gray-600 mb-2">Category breakdown:</div>
                {Object.entries(skill.categoryBreakdown).map(([category, count]) => (
                  <div key={category} className="flex justify-between text-xs">
                    <span>{skillsService.getCategoryDisplayName(category)}:</span>
                    <span>{count}</span>
                  </div>
                ))}
              </div>

              {skill.peopleWithSkill.length <= 10 && (
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="text-xs font-medium text-gray-600 mb-2">People:</div>
                  <div className="text-xs text-gray-700">
                    {skill.peopleWithSkill.join(', ')}
                  </div>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
              {error}
            </div>
          )}

          {activeTab === 'edit' ? (
            <form onSubmit={handleEdit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Edit skill name
                </label>
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  className="w-full p-3 border rounded-lg"
                  placeholder="Enter corrected skill name"
                  disabled={isReplacing}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Fix typos or make small corrections to the skill name
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isReplacing}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isReplacing}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
                
                <button
                  type="submit"
                  disabled={isReplacing || !editedName.trim() || editedName === skill?.skill}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
                >
                  {isReplacing ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Updating...
                    </>
                  ) : (
                    <>
                      <Edit2 size={16} />
                      Update Name
                    </>
                  )}
                </button>
              </div>
            </form>
          ) : (
            <form onSubmit={handleReplace} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">
                  Replace with existing skill
                </label>
                
                {/* Search input */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    placeholder="Search for existing skill..."
                    disabled={isReplacing}
                  />
                </div>

                {/* Skill suggestions */}
                {searchTerm && availableSkills.length > 0 && (
                  <div className="mt-2 border rounded-lg max-h-48 overflow-y-auto">
                    {availableSkills.map((availableSkill) => (
                      <button
                        key={availableSkill.skill}
                        type="button"
                        onClick={() => {
                          setSelectedSkill(availableSkill.skill);
                          setSearchTerm(availableSkill.skill);
                        }}
                        className={`w-full p-3 text-left hover:bg-gray-50 border-b last:border-b-0 flex items-center justify-between ${
                          selectedSkill === availableSkill.skill ? 'bg-blue-50 text-blue-700' : ''
                        }`}
                        disabled={isReplacing}
                      >
                        <div>
                          <span className="font-medium">{availableSkill.skill}</span>
                          <div className="text-xs text-gray-500 mt-1">
                            Used by {availableSkill.peopleWithSkill.length} people ({availableSkill.totalCount} total)
                          </div>
                        </div>
                        {selectedSkill === availableSkill.skill && (
                          <div className="text-blue-600">✓</div>
                        )}
                      </button>
                    ))}
                  </div>
                )}

                {/* Selected skill display */}
                {selectedSkill && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border border-blue-200">
                    <span className="text-sm text-blue-700">
                      Selected: <strong>{selectedSkill}</strong>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedSkill('');
                        setSearchTerm('');
                      }}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                      disabled={isReplacing}
                    >
                      <X size={14} />
                    </button>
                  </div>
                )}

                <p className="text-xs text-gray-500 mt-2">
                  Search and select an existing skill to replace "{skill.skill}" with
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isReplacing}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                
                <button
                  type="button"
                  onClick={handleDelete}
                  disabled={isReplacing}
                  className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 disabled:opacity-50 flex items-center gap-2"
                >
                  <Trash2 size={16} />
                  Delete
                </button>
                
                <button
                  type="submit"
                  disabled={isReplacing || !selectedSkill.trim()}
                  className="flex-1 bg-primary text-primary-foreground px-4 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-primary/90"
                >
                  {isReplacing ? (
                    <>
                      <Loader className="animate-spin" size={16} />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Replace size={16} />
                      Replace
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export const AdminSkillsManagement: React.FC = () => {
  const { appUser, permissions } = useAuth();
  const [skills, setSkills] = useState<SkillWithCounts[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<SkillWithCounts | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [error, setError] = useState('');

  useEffect(() => {
    if (permissions.canManageUsers) {
      loadSkills();
    }
  }, [permissions.canManageUsers]);

  const loadSkills = async () => {
    try {
      setLoading(true);
      const fetchedSkills = await skillsService.getAllSkillsWithCounts();
      setSkills(fetchedSkills);
    } catch (error) {
      console.error('Failed to load skills:', error);
      setError('Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  const handleReplaceSkill = (skill: SkillWithCounts) => {
    setSelectedSkill(skill);
    setShowReplaceModal(true);
  };

  const handleSuccess = () => {
    loadSkills();
  };

  const filteredSkills = skills.filter(skill => {
    const matchesSearch = skill.skill.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (selectedCategory === 'all') {
      return matchesSearch;
    }
    
    return matchesSearch && skill.categoryBreakdown[selectedCategory] > 0;
  });

  const skillCategories = skillsService.getSkillCategories();

  if (!permissions.canManageUsers) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="mx-auto h-16 w-16 text-yellow-500 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Denied</h2>
        <p className="text-gray-600">You don't have permission to manage skills.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader className="animate-spin" size={32} />
        <span className="ml-2">Loading skills...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Wrench size={28} className="text-primary" />
            Skills Management
          </h2>
          <p className="text-gray-600 mt-1">Manage skills across all team members</p>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-500">Total Skills</div>
          <div className="text-2xl font-bold text-primary">{skills.length}</div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 mr-3" />
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search skills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border rounded-lg w-full"
          />
        </div>

        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border rounded-lg bg-white"
        >
          <option value="all">All Categories</option>
          {skillCategories.map(category => (
            <option key={category.key} value={category.key}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      {/* Skills Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold">All Skills ({filteredSkills.length})</h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Skill Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  People Count
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Usage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Top Categories
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredSkills.map((skill) => (
                <tr key={skill.skill} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                        {skill.skill}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {skill.peopleWithSkill.length}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{skill.totalCount}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {Object.entries(skill.categoryBreakdown)
                      .sort(([,a], [,b]) => b - a)
                      .slice(0, 2)
                      .map(([category, count]) => 
                        `${skillsService.getCategoryDisplayName(category)} (${count})`
                      )
                      .join(', ')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleReplaceSkill(skill)}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded text-xs hover:bg-gray-200 flex items-center gap-1 ml-auto"
                    >
                      <Edit2 size={12} />
                      Manage
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Replace Skill Modal */}
      <ReplaceSkillModal
        isOpen={showReplaceModal}
        skill={selectedSkill}
        allSkills={skills}
        onClose={() => {
          setShowReplaceModal(false);
          setSelectedSkill(null);
        }}
        onSuccess={handleSuccess}
      />
    </div>
  );
};